import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useApolloClient } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'

import { CountrySelect } from 'Components/CountrySelect/CountrySelect'
import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import { Form } from 'Components/Form/Form'

import { getSelectValue, setFormErrors } from 'Utilities'

import { countryISOCodes } from 'Constants/constants'
import {
  E2E_ADDRESS_FORM_ADDRESS_TYPE,
  E2E_ADDRESS_FORM_CITY,
  E2E_ADDRESS_FORM_COUNTRY,
  E2E_ADDRESS_FORM_NAME,
  E2E_ADDRESS_FORM_NAME_ADDITION,
  E2E_ADDRESS_FORM_STREET,
  E2E_ADDRESS_FORM_STREET_NUMBER,
  E2E_ADDRESS_FORM_US_ADDRESS,
  E2E_ADDRESS_FORM_ZIPCODE,
} from 'Constants/e2e'
import {
  getAddressValidation,
  getAddressValidationVariables,
} from 'Constants/graphql/queries/validation/__generated__/getAddressValidation'
import { GET_ADDRESS_VALIDATION } from 'Constants/graphql/queries/validation/GetAddressValidation'

import { SHIPPING_ADDRESS_FORM_ZIPCODE_MIN_CHARACTERS } from './ShippingAddressForm.constants'
import {
  shippingAddressFormSchema,
  usShippingAddressFormSchema,
} from './ShippingAddressForm.schema'
import {
  ShippingAddressFormProps,
  ShippingAddressFormSchema,
} from './ShippingAddressForm.types'
import { shippingFormDefaultValues } from './ShippingAddressForm.values'
import { ShippingFormAddressTypes } from './ShippingAddressFormAddressTypes'

export const ShippingAddressForm = (props: ShippingAddressFormProps) => {
  const { defaultValues, rental, formId, onSubmit, onSetIsSubmitting } = props
  const client = useApolloClient()
  const [currentCountry, setCurrentCountry] = useState(
    defaultValues?.country ?? shippingFormDefaultValues.country,
  )

  const formMethods = useForm<ShippingAddressFormSchema>({
    defaultValues: defaultValues ?? shippingFormDefaultValues,
    resolver: zodResolver(
      currentCountry?.value === countryISOCodes.US
        ? usShippingAddressFormSchema
        : shippingAddressFormSchema,
    ),
  })

  const { reset, watch } = formMethods

  const handleSubmit = formMethods.handleSubmit(async data => {
    onSetIsSubmitting(true)

    try {
      const response = await client.query<
        getAddressValidation,
        getAddressValidationVariables
      >({
        query: GET_ADDRESS_VALIDATION,
        variables: {
          address: {
            city: data.city,
            country: getSelectValue(data.country),
            name: data.name,
            nameAddition: data.nameAddition,
            street:
              data.country?.value === countryISOCodes.US
                ? data.address
                : `${data.street} ${data.streetNumber}`,
            zipcode: data.zipcode,
          },
        },
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      if (
        response.data.addressValidation?.__typename ===
        'AddressValidationProblem'
      ) {
        setFormErrors<ShippingAddressFormSchema>(
          response.data,
          'addressValidation.invalidInputs',
          formMethods.setError,
        )

        onSetIsSubmitting(false)

        return
      }

      onSubmit(data)
    } catch (error) {
      onSetIsSubmitting(false)
    }
  })

  const country = watch('country')

  useEffect(() => {
    if (!defaultValues) {
      return
    }

    reset(defaultValues)
  }, [defaultValues, reset])

  useEffect(() => {
    setCurrentCountry(country)
  }, [country])

  const isUnitedStatesAddressType = currentCountry?.value === countryISOCodes.US

  return (
    <FormProvider {...formMethods}>
      <Form id={formId} onSubmit={handleSubmit}>
        <h6 className="m-0">
          <FormattedMessage id="ShippingAddressForm_heading" />
        </h6>

        <ShippingFormAddressTypes
          e2eSelector={E2E_ADDRESS_FORM_ADDRESS_TYPE}
          hasDefaultValues={!!defaultValues}
          rental={rental}
        />

        <Field
          containerClassName="mt-6"
          e2eSelector={E2E_ADDRESS_FORM_NAME}
          label="To"
          markAsRequired
          name="name"
          type={FieldTypeEnum.text}
        />

        <Field
          containerClassName="mt-2"
          e2eSelector={E2E_ADDRESS_FORM_NAME_ADDITION}
          label="Attn"
          name="nameAddition"
          type={FieldTypeEnum.text}
        />

        {isUnitedStatesAddressType ? (
          <Field
            containerClassName="mt-2"
            e2eSelector={E2E_ADDRESS_FORM_US_ADDRESS}
            label="Address"
            markAsRequired
            name="address"
            type={FieldTypeEnum.text}
          />
        ) : (
          <>
            <Field
              containerClassName="mt-2"
              e2eSelector={E2E_ADDRESS_FORM_STREET}
              label="Street_name"
              markAsRequired
              name="street"
              type={FieldTypeEnum.text}
            />

            <Field
              containerClassName="mt-2"
              e2eSelector={E2E_ADDRESS_FORM_STREET_NUMBER}
              label="Street_number"
              markAsRequired
              name="streetNumber"
              type={FieldTypeEnum.text}
            />
          </>
        )}

        {isUnitedStatesAddressType ? (
          <>
            <Field
              containerClassName="mt-2"
              e2eSelector={E2E_ADDRESS_FORM_CITY}
              label="CityState"
              markAsRequired
              name="city"
              type={FieldTypeEnum.text}
            />

            <Field
              containerClassName="mt-2"
              e2eSelector={E2E_ADDRESS_FORM_ZIPCODE}
              feedbackTranslationValues={{
                amount: SHIPPING_ADDRESS_FORM_ZIPCODE_MIN_CHARACTERS,
              }}
              label="Zip"
              markAsRequired
              name="zipcode"
              type={FieldTypeEnum.text}
            />
          </>
        ) : (
          <>
            <Field
              containerClassName="mt-2"
              e2eSelector={E2E_ADDRESS_FORM_ZIPCODE}
              feedbackTranslationValues={{
                amount: SHIPPING_ADDRESS_FORM_ZIPCODE_MIN_CHARACTERS,
              }}
              label="Postcode"
              markAsRequired
              name="zipcode"
              type={FieldTypeEnum.text}
            />

            <Field
              containerClassName="mt-2"
              e2eSelector={E2E_ADDRESS_FORM_CITY}
              label="City"
              markAsRequired
              name="city"
              type={FieldTypeEnum.text}
            />
          </>
        )}

        <CountrySelect
          containerClassName="mt-2"
          e2eSelector={E2E_ADDRESS_FORM_COUNTRY}
          label="Country"
          markAsRequired
          name="country"
        />
      </Form>
    </FormProvider>
  )
}
